import React from 'react'
import {
  PiPencilSimple,
  PiCopySimple,
  PiLightning,
  PiLightningSlash,
  PiTrash,
  PiPause,
  PiPlay,
  PiArrowsClockwise,
  PiArrowSquareOut,
  PiFolderOpen,
  PiDownloadSimple,
} from 'react-icons/pi'
import { EllipsisVertical } from 'lucide-react'
import { cn } from '../UI/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../UI/BaseDropdown/dropdown-base'
import { useModalDepth } from '../UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from '../../styles/z-index'

interface ActionItem {
  label: string
  icon: React.ComponentType<{ size?: number; className?: string }>
  onClick: () => void
  variant?: 'default' | 'destructive'
  disabled?: boolean
  disabledReason?: string
  hasSeparatorAfter?: boolean
  /** When provided, renders as a nested submenu instead of a regular menu item. */
  subContent?: React.ReactNode
}

interface ActionsDropdownProps {
  actions: ActionItem[]
  disabled?: boolean
  className?: string
  containerWidth?: number
}

export function ActionsDropdown({
  actions,
  className,
  disabled,
  containerWidth = 120,
}: ActionsDropdownProps) {
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  return (
    <div className={cn('relative flex justify-end', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <button
            type="button"
            data-actions-trigger="true"
            className={cn(
              'flex items-center justify-center w-5 h-5 text-fg-secondary hover:text-fg focus:outline-none focus:text-fg',
              {
                'opacity-50 cursor-not-allowed': disabled,
              }
            )}
            aria-label="Actions"
            onClick={(e) => e.stopPropagation()}
          >
            <EllipsisVertical
              className={cn({
                'cursor-not-allowed': disabled,
              })}
              size={20}
              strokeWidth={2.5}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          style={{ minWidth: Math.max(containerWidth, 160), zIndex }}
          className="!rounded-xl !border-0 !bg-white !p-1.5 !shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.10),0px_2px_4px_-2px_rgba(16,24,40,0.06)]"
        >
          {actions.map((action, index) => {
            const isDestructive = action.variant === 'destructive'
            const previousIsDestructive =
              index > 0 && actions[index - 1]?.variant === 'destructive'
            // Auto-insert a divider before the first destructive item so
            // "Delete" sits in its own section without the caller having to
            // set hasSeparatorAfter on the previous action. Skip when the
            // destructive item is the only/first one — there's nothing above
            // it to separate from.
            const needsSeparatorBefore =
              isDestructive && index > 0 && !previousIsDestructive
            return (
              <React.Fragment key={index}>
                {needsSeparatorBefore && (
                  <DropdownMenuSeparator className="!my-1.5 !mx-0 !h-px !bg-[#e5e7eb]" />
                )}
                {action.subContent && !action.disabled ? (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className="!flex !cursor-pointer !items-center !gap-2.5 !rounded-lg !px-3 !py-2 !text-base !font-medium !tracking-[-0.2px] !text-[#374151] hover:!bg-[#f3f4f6] hover:!text-neutral-900 focus:!bg-[#f3f4f6] focus:!text-neutral-900">
                      <action.icon
                        size={18}
                        className="flex-shrink-0 text-current"
                      />
                      <span className="truncate">{action.label}</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="!rounded-xl !border-0 !bg-white !p-1.5 !shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.10),0px_2px_4px_-2px_rgba(16,24,40,0.06)]">
                      {action.subContent}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ) : (
                  <DropdownMenuItem
                    disabled={action.disabled}
                    title={action.disabled ? action.disabledReason : undefined}
                    className={cn(
                      '!flex !cursor-pointer !items-center !gap-2.5 !rounded-lg !px-3 !py-2 !text-base !font-medium !tracking-[-0.2px]',
                      isDestructive
                        ? '!bg-[#fef2f2] !text-[#b91c1c] hover:!bg-[#fee2e2] hover:!text-[#991b1b] focus:!bg-[#fee2e2] focus:!text-[#991b1b]'
                        : '!text-[#374151] hover:!bg-[#f3f4f6] hover:!text-neutral-900 focus:!bg-[#f3f4f6] focus:!text-neutral-900',
                      action.disabled && '!opacity-50 !cursor-not-allowed'
                    )}
                    onSelect={() => {
                      if (!action.disabled) {
                        action.onClick()
                      }
                    }}
                  >
                    <action.icon
                      size={18}
                      className="flex-shrink-0 text-current"
                    />
                    <span className="truncate">{action.label}</span>
                  </DropdownMenuItem>
                )}
                {action.hasSeparatorAfter && (
                  <DropdownMenuSeparator className="!my-1.5 !mx-0 !h-px !bg-[#e5e7eb]" />
                )}
              </React.Fragment>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

// Pre-configured actions for common use cases
export const createEditAction = (
  onClick: () => void,
  label = 'Edit'
): ActionItem => ({
  label,
  icon: PiPencilSimple,
  onClick,
})

export const createCopyAction = (onClick: () => void): ActionItem => ({
  label: 'Copy',
  icon: PiCopySimple,
  onClick,
})

export const createPauseAction = (onClick: () => void): ActionItem => ({
  label: 'Pause',
  icon: PiPause,
  onClick,
})

export const createPlayAction = (onClick: () => void): ActionItem => ({
  label: 'Resume',
  icon: PiPlay,
  onClick,
})

export const createSyncAction = (
  onClick: () => void,
  disabled?: boolean
): ActionItem => ({
  label: 'Sync',
  icon: PiArrowsClockwise,
  onClick,
  disabled,
})

export const createExternalAction = (
  label: string,
  onClick: () => void
): ActionItem => ({
  label,
  icon: PiArrowSquareOut,
  onClick,
})

export const createDownloadAction = (
  label: string,
  onClick: () => void
): ActionItem => ({
  label,
  icon: PiDownloadSimple,
  onClick,
})

export const createToggleActiveAction = (
  onClick: () => void,
  isActive: boolean,
  isDisabled = false
): ActionItem => ({
  label: isActive ? 'Disable' : 'Enable',
  icon: isActive ? PiLightningSlash : PiLightning,
  onClick,
  disabled: isDisabled,
})

export const createDeleteAction = (onClick: () => void): ActionItem => ({
  label: 'Delete',
  icon: PiTrash,
  onClick,
  variant: 'destructive' as const,
})

export const createManageFilesAction = (onClick: () => void): ActionItem => ({
  label: 'Manage Files',
  icon: PiFolderOpen,
  onClick,
})
